import { useEffect } from "react";
import { useBrandConfig } from "@/hooks/use-brand-config";

/**
 * Dynamically updates document title, meta tags, and theme colors
 * based on the resolved brand entity.
 */
const BrandMeta = () => {
  const brand = useBrandConfig();

  useEffect(() => {
    if (!brand.isLoaded) return;

    const tagline = brand.brandTagline || "Compare Personal Loans & Get Funded Fast";
    const fullTitle = `${brand.brandName} | ${tagline}`;

    // Update document title
    document.title = fullTitle;

    // Update meta author
    const authorMeta = document.querySelector('meta[name="author"]');
    if (authorMeta) authorMeta.setAttribute("content", brand.brandName);

    // Update meta description
    const descMeta = document.querySelector('meta[name="description"]');
    if (descMeta) descMeta.setAttribute("content", `${tagline}. No hidden fees, no credit score impact, and funding as fast as next business day. Check your rate in minutes.`);

    // Update canonical link
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `https://${brand.brandDomain}`);

    // Update OG title
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute("content", fullTitle);

    // Update OG description
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute("content", `${tagline}. No hidden fees, no credit score impact, and funding as fast as next business day.`);

    // Update OG image if configured
    if (brand.ogImageUrl) {
      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage) ogImage.setAttribute("content", brand.ogImageUrl);
      const twitterImage = document.querySelector('meta[name="twitter:image"]');
      if (twitterImage) twitterImage.setAttribute("content", brand.ogImageUrl);
    }

    // Update twitter site handle
    const twitterSite = document.querySelector('meta[name="twitter:site"]');
    if (twitterSite) twitterSite.setAttribute("content", "");

    // Update favicon if configured
    if (brand.faviconUrl) {
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) favicon.setAttribute("href", brand.faviconUrl);
    }

    // Update loading placeholder text
    const placeholder = document.getElementById("fcp-placeholder");
    if (placeholder) {
      const loadingText = placeholder.querySelector("p");
      if (loadingText) loadingText.textContent = `Loading ${brand.brandName}…`;
    }

    // Apply dynamic theme colors from brand_color_primary
    if (brand.brandColorPrimary) {
      applyBrandColors(brand.brandColorPrimary);
    }
  }, [brand]);

  return null;
};

/**
 * Parses a brand color (HSL string like "168 76% 26%" or hex like "#0f9b7a")
 * and injects CSS custom properties onto :root to re-theme the entire app.
 */
function applyBrandColors(colorValue: string) {
  const root = document.documentElement;

  let h: number, s: number, l: number;

  if (colorValue.startsWith("#")) {
    // Parse hex
    const hex = colorValue.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    l = (max + min) / 2;
    if (max === min) { h = 0; s = 0; } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
        case g: h = ((b - r) / d + 2) * 60; break;
        default: h = ((r - g) / d + 4) * 60; break;
      }
    }
    s = Math.round(s * 100);
    l = Math.round(l * 100);
    h = Math.round(h);
  } else {
    // Parse HSL string like "168 76% 26%"
    const parts = colorValue.replace(/%/g, "").split(/\s+/);
    if (parts.length < 3) return;
    h = parseInt(parts[0]);
    s = parseInt(parts[1]);
    l = parseInt(parts[2]);
  }

  if (isNaN(h) || isNaN(s) || isNaN(l)) return;

  // Clamp lightness to max 28% to ensure WCAG AA 4.5:1 contrast against white
  l = Math.min(l, 28);

  // Primary color
  root.style.setProperty("--primary", `${h} ${s}% ${l}%`);
  root.style.setProperty("--ring", `${h} ${s}% ${l}%`);

  // Accent (lighter version)
  root.style.setProperty("--accent", `${h} ${Math.max(s - 6, 0)}% ${Math.min(l + 16, 90)}%`);

  // Teal custom tokens
  root.style.setProperty("--teal", `${h} ${s}% ${l}%`);
  root.style.setProperty("--teal-light", `${h} ${Math.max(s - 6, 0)}% ${Math.min(l + 19, 90)}%`);
  root.style.setProperty("--emerald", `${Math.max(h - 16, 0)} ${Math.max(s - 7, 0)}% ${Math.min(l + 16, 90)}%`);

  // Gradients
  const emeraldH = Math.max(h - 16, 0);
  const emeraldS = Math.max(s - 7, 0);
  const emeraldL = Math.min(l + 6, 80);
  root.style.setProperty("--gradient-cta", `linear-gradient(135deg, hsl(${h} ${s}% ${l}%) 0%, hsl(${emeraldH} ${emeraldS}% ${emeraldL}%) 100%)`);

  // Shadows
  root.style.setProperty("--shadow-cta", `0 8px 24px -4px hsl(${h} ${s}% ${Math.min(l + 10, 80)}% / 0.35)`);
  root.style.setProperty("--shadow-glow", `0 0 40px hsl(${h} ${s}% ${Math.min(l + 10, 80)}% / 0.2)`);
}

export default BrandMeta;
